import React, { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { MyHrMassRoleDialog } from "my-hr/components";
import { MY_HR_ADD_MASS_ROLE, MY_HR_GET_MODULE_ROLES } from "my-hr/graphql";
import { OcConfirmationDialog, OcRoleFiltering } from "oc/components";
import { useOcClientState, useOcPolling } from "oc/hooks";
import { RoleFilterInput } from "oc/types";
import getModuleIcon from "oc/utils/get-modul-icon";

interface ModuleRole {
  module: string;
  roles: string[];
}

interface GetModuleRolesData {
  moduleRoles: ModuleRole[];
}

interface AddMassRoleVars {
  roleFilters: RoleFilterInput;
  roleFiltering: RoleFilterInput;
  module: string | undefined;
  role: string;
}

interface AddMassRoleResult {
  addMassRole: string;
}

// Component props
interface MyHrAddMassRoleProps {
  roleFilters: RoleFilterInput;
  roleFiltering: RoleFilterInput;
  onAddFiltering: ({ key, value }: { key: string; value: any }) => void;
  onRemoveFiltering: ({ key, value }: { key: string; value: any }) => void;
  isMobile: boolean;
}

// Styled Select component with explicit generic
const StyledSelect = styled(Select) <SelectProps<string>>`
  .MuiOutlinedInput-input {
    padding: 16px 14px;
    background-color: white;
  }
`;

type Result = {
  errors?: any[];
  processed?: number;
  added?: number;
};

export const MyHrAddMassRole: React.FC<MyHrAddMassRoleProps> = ({
  roleFilters,
  roleFiltering,
  onAddFiltering,
  onRemoveFiltering,
  isMobile,
}) => {
  const { t } = useTranslation();

  // Client state hooks
  const [selectedProduct, setSelectedProduct] = useOcClientState<string>(
    "selectedModule",
    "MyHR",
  );
  const [selectedRole, setSelectedRole] = useOcClientState<string>(
    "selectedRole",
    "",
  );
  const [result, setResult] = useState<Result | null>(null);

  // Local state
  const [showRoleFiltering, setShowRoleFiltering] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showConfirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false);

  // Fetch module roles
  const { data: productsData } = useQuery<GetModuleRolesData>(
    MY_HR_GET_MODULE_ROLES,
    {
      fetchPolicy: "cache-and-network",
      pollInterval: useOcPolling(1000 * 60),
    },
  );

  const selectedModule = productsData?.moduleRoles.find(
    (item) => item.module === selectedProduct,
  );
  const availableRoles = selectedModule?.roles ?? [];

  // Mutation for adding mass role
  const [addMassRole] = useMutation<AddMassRoleResult, AddMassRoleVars>(
    MY_HR_ADD_MASS_ROLE,
    {
      onCompleted: (data) => {
        // Try to parse the result or set a default Result object
        try {
          const parsed: Result =
            typeof data.addMassRole === "string"
              ? JSON.parse(data.addMassRole)
              : data.addMassRole;
          setResult(parsed);
        } catch {
          setResult({
            errors: [{ message: data.addMassRole }],
            processed: 0,
            added: 0,
          });
        }
      },
      onError: (error) => console.error(error),
    },
  );

  // Handle module selection change
  const handleChange = (
    event: SelectChangeEvent<unknown>,
    _child: ReactNode,
  ): void => {
    const value = event.target.value as string;
    setSelectedProduct(value);
  };

  const handleCancel = (): void => {
    setSelectedProduct("MyHR");
    setSelectedRole("");
    setResult(null);
  };

  const handleShowDialog = (): void => setShowDialog((prev) => !prev);
  const handleCloseImportDialog = (): void => setShowDialog(false);
  const handleFinish = (): void => {
    setShowDialog(false);
    handleCancel();
  };

  const handleAddRole = (): void => {
    const variables: AddMassRoleVars = {
      roleFilters,
      roleFiltering,
      module: selectedModule?.module,
      role: selectedRole,
    };
    setShowDialog(true);
    setShowRoleFiltering(false);
    addMassRole({ variables });
  };

  const handleShowRoleFiltering = (state: boolean): void => {
    setShowRoleFiltering(state);
  };

  const handleShowConfirmationDialog = (): void =>
    setShowConfirmationDialog(true);
  const handleOKConfirmationDialog = (): void => {
    setShowConfirmationDialog(false);
    handleAddRole();
  };
  const handleCancelConfirmationDialog = (): void =>
    setShowConfirmationDialog(false);

  const generateConfirmationText = (): string => {
    const empty =
      (roleFilters.company?.length ?? 0) === 0 &&
      (roleFilters.orgUnit?.length ?? 0) === 0 &&
      (roleFilters.workingPlace?.length ?? 0) === 0 &&
      (roleFilters.jobClassGroup?.length ?? 0) === 0 &&
      (roleFilters.payOffice?.length ?? 0) === 0 &&
      (roleFilters.contract?.length ?? 0) === 0;
    return empty
      ? "MY_HR_ADD_GROUP_ROLE_TO_ALL_DIALOG_TEXT"
      : "MY_HR_ADD_GROUP_ROLE_DIALOG_TEXT";
  };

  // Effects
  useEffect(() => {
    return () => {
      handleCancel();
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedRole("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  return (
    <Box>
      <OcConfirmationDialog
        showDialog={showConfirmationDialog}
        title={t("MY_HR_ADD_GROUP_ROLE_DIALOG_TITLE")}
        texts={[t(generateConfirmationText())]}
        okButtonText={t("OC_SAVE")}
        cancelButtonText={t("OC_CANCEL")}
        onClose={handleOKConfirmationDialog}
        onCancel={handleCancelConfirmationDialog}
      />

      <MyHrMassRoleDialog
        onShowDialog={handleShowDialog}
        showDialog={showDialog}
        onFinish={handleFinish}
        onCloseDialog={handleCloseImportDialog}
        result={result}
        infoText={t("MY_HR_ADD_MASS_ROLE_INFO")}
        successText={t("MY_HR_ADD_ROLE_SUCCEEDED")}
        successInfo={t("MY_HR_ROLE_ADDED")}
        toolbarLabel={t("MY_HR_ADD_MASS_ROLE")}
        errorText={t("MY_HR_ADD_ROLE_ERROR")}
        action="add"
        isMobile={isMobile}
      />

      <OcRoleFiltering
        showRoleFiltering={showRoleFiltering}
        setShowRoleFiltering={handleShowRoleFiltering}
        onAddRole={handleShowConfirmationDialog}
        product={selectedModule?.module ?? ""}
        role={selectedRole}
        roleFiltering={roleFiltering}
        onAddRoleFiltering={onAddFiltering}
        onRemoveRoleFiltering={onRemoveFiltering}
        isMobile={isMobile}
      />

      <Collapse in={!showRoleFiltering} timeout="auto" unmountOnExit>
        <Box display="flex" flexDirection="column" alignItems="center" pt={3}>
          <Card
            variant="outlined"
            style={{
              width: isMobile ? "100%" : 696,
              borderRadius: 1,
              borderColor: "white",
              overflow: "visible",
              marginBottom: "16px",
              backgroundColor: "#F1F3F4",
            }}
          >
            <Box p={2} display="flex" flexDirection="column">
              <Box
                fontSize="20px"
                letterSpacing="0.15px"
                padding="15px 0px"
                paddingBottom="30px"
              >
                {t("MY_HR_ADD_ROLE")}
              </Box>
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                paddingBottom="8px"
              >
                <Box width={isMobile ? "100%" : "50%"} paddingRight="8px">
                  <FormControl
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    style={{ backgroundColor: "white" }}
                  >
                    <InputLabel id="module-select-label">
                      {t("OC_MODULE")}
                    </InputLabel>
                    <StyledSelect
                      labelId="module-select-label"
                      id="add-mass-role-input"
                      value={selectedProduct}
                      onChange={handleChange}
                      label={t("OC_MODULE")}
                    >
                      {productsData?.moduleRoles.map((module) => (
                        <MenuItem key={module.module} value={module.module}>
                          <Box display="flex" alignItems="center">
                            {getModuleIcon(module.module)}
                            <Typography
                              variant="body1"
                              style={{
                                marginLeft: "22px",
                              }}
                            >
                              {module.module}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                </Box>
                {isMobile && <Box height="16px" />}
                <Box
                  width={isMobile ? "100%" : "50%"}
                  paddingLeft={isMobile ? "0px" : "8px"}
                >
                  <ButtonGroup
                    orientation="vertical"
                    aria-label="vertical outlined primary button group"
                    fullWidth
                    style={{
                      color: "#E3E5E5",
                    }}
                    variant="contained"
                    disableElevation
                  >
                    {availableRoles.map((role) => (
                      <Button
                        key={role}
                        id={role}
                        onClick={() => setSelectedRole(role)}
                        disableElevation
                        color="secondary"
                        variant="text"
                        disabled={role === "admin"}
                        style={
                          role === selectedRole
                            ? {
                              backgroundColor: "#cc0033",
                              color: "white",
                            }
                            : {
                              backgroundColor: "white",
                              color: role === "admin" ? "#C6CACC" : "#212121",
                            }
                        }
                      >
                        {t(`MY_HR_ROLE_${role.toUpperCase()}`)}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection={isMobile ? "column-reverse" : "row"}
                justifyContent="flex-end"
                padding={isMobile ? "0px" : "10px 0px 0px 0px"}
              >
                <Button
                  id="my-hr-add-user-role-button-cancel"
                  onClick={handleCancel}
                  color="secondary"
                  variant="text"
                >
                  {t("OC_CANCEL")}
                </Button>
                {isMobile && <Box height="16px" />}
                <Button
                  id="my-hr-add-user-role-button"
                  variant="contained"
                  color="primary"
                  disableElevation
                  style={{ marginLeft: isMobile ? "0px" : "16px" }}
                  disabled={!selectedRole}
                  onClick={() => setShowRoleFiltering(true)}
                >
                  {t("OC_ADD")}
                </Button>
                {isMobile && <Box height="16px" />}
              </Box>
            </Box>
          </Card>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MyHrAddMassRole;
